import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getExpoBrands,
  saveExpoBrand,
  updateExpoBrand,
  deleteExpoBrand,
} from "../Apis/ExpoBrandAPIs";
import { uploadFile } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import TablePagination from "./TablePagination";

const ExpoBrandLayer = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Add/Edit Modal state
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [brandYearsList, setBrandYearsList] = useState([]); // [{ year: number, logo: string }]
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedYearLogo, setSelectedYearLogo] = useState("");
  const [isUploadingYearLogo, setIsUploadingYearLogo] = useState(false);
  const yearLogoFileInputRef = useRef(null);
  const [tempLogos, setTempLogos] = useState([]); // [{ image: string, companyName: string }]
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New logo entry state inside modal
  const [newCompanyName, setNewCompanyName] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoFileInputRef = useRef(null);

  // Delete Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch brand list
  const fetchBrands = async () => {
    setLoading(true);
    const res = await getExpoBrands({
      page: currentPage,
      limit: rowsPerPage,
      search: debouncedSearch,
    });
    if (res.status) {
      setBrands(res.response?.data || res.response || []);
      setTotalRecords(
        res.response?.total || (res.response?.data || res.response || []).length
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchBrands();
  }, [currentPage, rowsPerPage, debouncedSearch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleBrandNameChange = (val) => {
    setBrandName(val);
    if (modalMode === "add" && val.trim() !== "") {
      const existing = brands.find(
        (b) => b.name?.toLowerCase().trim() === val.toLowerCase().trim()
      );
      if (existing) {
        setTempLogos(existing.logos || []);
        setBrandYearsList(existing.years || []);
      }
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedBrand(null);
    setBrandName("");
    setBrandYearsList([]);
    setSelectedYear(new Date().getFullYear());
    setSelectedYearLogo("");
    setTempLogos([]);
    setNewCompanyName("");
    setShowBrandModal(true);
  };

  const openEditModal = (brand) => {
    setModalMode("edit");
    setSelectedBrand(brand);
    setBrandName(brand.name || "");
    setBrandYearsList(brand.years || []);
    setSelectedYear(new Date().getFullYear());
    setSelectedYearLogo("");
    setTempLogos(brand.logos || []);
    setNewCompanyName("");
    setShowBrandModal(true);
  };

  const closeBrandModal = () => {
    setShowBrandModal(false);
    setSelectedBrand(null);
    setBrandName("");
    setBrandYearsList([]);
    setSelectedYear(new Date().getFullYear());
    setSelectedYearLogo("");
    setTempLogos([]);
    setNewCompanyName("");
  };

  // Handle Logo Uploading
  const handleLogoFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingLogo(true);
    let successCount = 0;
    const newUploadedLogos = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", "brands");

        const uploadRes = await uploadFile(formData);
        if (uploadRes.status) {
          const path =
            uploadRes.response?.path ||
            uploadRes.response?.data?.path ||
            uploadRes.response;

          const companyName = files.length === 1 && newCompanyName.trim()
            ? newCompanyName.trim()
            : file.name.split(".")[0];

          newUploadedLogos.push({
            image: path,
            companyName: companyName,
          });
          successCount++;
        }
      }

      if (successCount > 0) {
        setTempLogos([...tempLogos, ...newUploadedLogos]);
        setNewCompanyName("");
        toast.success(`${successCount} logo(s) uploaded and added successfully!`);
      } else {
        toast.error("Failed to upload image(s). Please try again.");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Error uploading logo image(s).");
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = "";
    }
  };

  const handleRemoveTempLogo = (indexToRemove) => {
    setTempLogos(tempLogos.filter((_, idx) => idx !== indexToRemove));
    toast.info("Logo removed");
  };

  // Handle Submit Form
  const handleSubmitBrand = async () => {
    if (!brandName.trim()) {
      toast.error("Brand name is required");
      return;
    }
    if (brandYearsList.length === 0) {
      toast.error("Please add at least one year and its logo");
      return;
    }

    setIsSubmitting(true);
    const data = {
      name: brandName.trim(),
      years: brandYearsList,
      logos: tempLogos,
    };

    let res;
    if (modalMode === "add") {
      res = await saveExpoBrand(data);
    } else {
      res = await updateExpoBrand(selectedBrand._id || selectedBrand.id, data);
    }

    if (res.status) {
      toast.success(
        `Expo Brand ${modalMode === "add" ? "created" : "updated"} successfully!`
      );
      fetchBrands();
      closeBrandModal();
    }
    setIsSubmitting(false);
  };

  // Delete handlers
  const confirmDelete = (brand) => {
    setBrandToDelete(brand);
    setShowDeleteModal(true);
  };

  const handleDeleteBrand = async () => {
    if (!brandToDelete) return;
    setIsDeleting(true);
    const res = await deleteExpoBrand(brandToDelete._id || brandToDelete.id);
    if (res.status) {
      toast.success("Expo Brand deleted successfully!");
      fetchBrands();
      setShowDeleteModal(false);
      setBrandToDelete(null);
    }
    setIsDeleting(false);
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm overflow-hidden">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/past-expo"
            className="text-white d-flex align-items-center"
            title="Back to Past Expos"
          >
            <Icon icon="solar:arrow-left-bold" className="text-xl me-2 text-white" />
          </Link>
          <h6 className="text-white mb-0 fw-bold">Manage Expo Brands & Logos</h6>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative">
            <input
              type="text"
              className="form-control radius-8 ps-40 py-10 bg-white border-0"
              style={{ height: "40px", fontSize: "13px" }}
              placeholder="Search Brands..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Icon
              icon="solar:magnifer-linear"
              className="position-absolute start-0 top-50 translate-middle-y ms-12 text-black opacity-75"
            />
          </div>
          <button
            onClick={openAddModal}
            className="btn text-sm btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2"
            style={{
              backgroundColor: "#f7e7da",
              color: "black",
              fontWeight: "600",
              height: "40px",
              border: "none",
            }}
          >
            <Icon icon="ic:baseline-plus" className="text-lg" />
            Add Brand
          </button>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive border radius-8 overflow-hidden mb-16 shadow-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr
                style={{
                  background: "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                }}
              >
                <th scope="col" className="fw-bold text-dark py-16 px-20">
                  S.No
                </th>
                <th scope="col" className="fw-bold text-dark py-16 px-20">
                  Year-wise Logos
                </th>
                <th scope="col" className="fw-bold text-dark py-16 px-20">
                  Brand Name
                </th>
                <th scope="col" className="fw-bold text-dark py-16 px-20 text-center">
                  Years
                </th>
                <th scope="col" className="fw-bold text-dark py-16 px-20 text-center">
                  Total Client Logos
                </th>
                <th scope="col" className="fw-bold text-dark py-16 px-20 text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-24">
                    Loading...
                  </td>
                </tr>
              ) : brands.length > 0 ? (
                brands.map((item, index) => (
                  <tr key={item._id || item.id} className="transition-all hover-bg-light">
                    <td className="py-12 px-20 text-xs text-secondary-light">
                      {currentPage * rowsPerPage + index + 1}
                    </td>
                    <td className="py-12 px-20">
                      <div className="d-flex flex-wrap gap-4">
                        {(item.years || []).map((yrLogo, idx) => (
                          <div
                            key={idx}
                            className="border radius-4 overflow-hidden bg-white position-relative"
                            style={{ width: "36px", height: "36px" }}
                            title={`${item.name} (${yrLogo.year})`}
                          >
                            <img
                              src={getImageUrl(yrLogo.logo)}
                              alt={yrLogo.year}
                              className="object-fit-contain w-100 h-100"
                            />
                            <span
                              className="position-absolute bottom-0 end-0 bg-dark text-white fw-bold px-2"
                              style={{ fontSize: "7px", opacity: 0.8 }}
                            >
                              {yrLogo.year}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="py-12 px-20 text-sm fw-semibold text-dark">
                      {item.name}
                    </td>
                    <td className="py-12 px-20 text-center text-sm fw-medium text-dark">
                      {(item.years || []).map(y => y.year).join(", ") || "-"}
                    </td>
                    <td className="py-12 px-20 text-center fw-bold">
                      <span className="badge bg-danger-focus text-danger-600 px-16 py-6 radius-4">
                        {item.logos?.length || 0} Logos
                      </span>
                    </td>
                    <td className="py-12 px-20 text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button
                          onClick={() => openEditModal(item)}
                          className="glass-action-btn glass-btn-edit border-0"
                          title="Edit Brand & Logos"
                        >
                          <Icon icon="lucide:edit" className="text-lg" />
                        </button>
                        <button
                          onClick={() => confirmDelete(item)}
                          className="glass-action-btn glass-btn-delete border-0"
                          title="Delete Brand"
                          style={{
                            backgroundColor: "#fee2e2",
                            color: "#ef4444",
                          }}
                        >
                          <Icon icon="lucide:trash-2" className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-24">
                    No brands found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>
      {/* CREATE / EDIT MODAL */}
      <Modal show={showBrandModal} onHide={closeBrandModal} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold text-dark fs-5">
            {modalMode === "add" ? "Create Expo Brand" : "Edit Brand Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24">
          <div className="mb-20">
            <label className="form-label text-sm fw-bold text-dark uppercase mb-8">
              Brand Name (e.g. BUILD EXPO, PROPERTY EXPO)
            </label>
            <input
              type="text"
              className="form-control radius-8 border-neutral-200"
              placeholder="Enter brand name"
              value={brandName}
              onChange={(e) => handleBrandNameChange(e.target.value)}
              style={{ height: "48px" }}
              disabled={modalMode === "edit"}
            />
          </div>

          <div className="mb-24 p-16 radius-8 bg-neutral-50 border">
            <h6 className="fw-bold text-dark mb-16 text-sm">
              <Icon icon="solar:calendar-bold" className="text-danger me-2 text-lg" />
              Manage Year-wise Brand Logos
            </h6>
            <div className="row g-3 align-items-end mb-16">
              <div className="col-md-3">
                <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-6">
                  Select Year
                </label>
                <select
                  className="form-select radius-8 border-neutral-200"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{ height: "44px" }}
                >
                  {Array.from({ length: 16 }, (_, i) => 2020 + i).map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-6">
                  Upload Logo for Selected Year
                </label>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  ref={yearLogoFileInputRef}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setIsUploadingYearLogo(true);
                    try {
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("folder", "brands");
                      const uploadRes = await uploadFile(formData);
                      if (uploadRes.status) {
                        const path = uploadRes.response?.path || uploadRes.response?.data?.path || uploadRes.response;
                        setSelectedYearLogo(path);
                        toast.success("Logo uploaded successfully!");
                      } else {
                        toast.error("Failed to upload logo.");
                      }
                    } catch (err) {
                      console.error("Logo upload error:", err);
                      toast.error("Error uploading logo.");
                    } finally {
                      setIsUploadingYearLogo(false);
                      if (yearLogoFileInputRef.current) yearLogoFileInputRef.current.value = "";
                    }
                  }}
                />
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    disabled={isUploadingYearLogo}
                    onClick={() => yearLogoFileInputRef.current?.click()}
                    className="btn btn-outline-secondary w-100 radius-8 fw-semibold d-flex align-items-center justify-content-center gap-2"
                    style={{ height: "44px" }}
                  >
                    <Icon icon="solar:upload-linear" className="text-xl" />
                    {isUploadingYearLogo ? "Uploading..." : "Select Logo"}
                  </button>
                  {selectedYearLogo && (
                    <div className="border radius-8 p-4 bg-white" style={{ width: "44px", height: "44px" }}>
                      <img
                        src={getImageUrl(selectedYearLogo)}
                        alt="Preview"
                        className="object-fit-contain w-100 h-100"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedYearLogo) {
                      toast.error("Please upload a logo first");
                      return;
                    }
                    if (brandYearsList.some(item => item.year === selectedYear)) {
                      toast.error(`Logo for ${selectedYear} is already added. Remove the existing one first.`);
                      return;
                    }
                    setBrandYearsList([...brandYearsList, { year: selectedYear, logo: selectedYearLogo }]);
                    setSelectedYearLogo("");
                    toast.success(`Logo for year ${selectedYear} added to list!`);
                  }}
                  className="btn btn-danger w-100 radius-8 fw-semibold"
                  style={{ height: "44px", backgroundColor: "#ed1c24", border: "none" }}
                >
                  Add Year Logo
                </button>
              </div>
            </div>

            <div className="mt-16">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-8">
                Added Year Logos ({brandYearsList.length})
              </label>
              {brandYearsList.length > 0 ? (
                <div className="d-flex flex-wrap gap-12 p-12 border radius-8 bg-white" style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {brandYearsList.map((item, idx) => (
                    <div
                      key={idx}
                      className="position-relative border radius-8 p-8 d-flex align-items-center gap-8 bg-light"
                      style={{ width: "160px", height: "50px" }}
                    >
                      <div className="border radius-4 overflow-hidden bg-white" style={{ width: "34px", height: "34px" }}>
                        <img
                          src={getImageUrl(item.logo)}
                          alt={item.year}
                          className="object-fit-contain w-100 h-100"
                        />
                      </div>
                      <span className="text-xs fw-bold text-dark">{item.year}</span>
                      <button
                        type="button"
                        onClick={() => setBrandYearsList(brandYearsList.filter((_, i) => i !== idx))}
                        className="position-absolute btn btn-xs btn-danger p-4 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          top: "-6px",
                          right: "-6px",
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#ef4444",
                        }}
                        title="Remove"
                      >
                        <Icon icon="ph:x-bold" className="text-xxs text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-16 bg-white border border-dashed radius-8 text-secondary-light text-xs">
                  No year logos added yet.
                </div>
              )}
            </div>
          </div>

          <hr className="my-24" />

          {/* Logo Addition Section */}
          <div className="mb-20">
            <h6 className="fw-bold text-dark mb-16 text-sm">
              <Icon icon="solar:gallery-bold" className="text-danger me-2 text-lg" />
              Add Client Logos
            </h6>
            <div className="row g-3 align-items-end p-16 radius-8 bg-neutral-50 border">
              <div className="col-md-7">
                <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-6">
                  Company / Exhibitor Name
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="e.g. Ocean Softwares"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  style={{ height: "44px" }}
                />
              </div>
              <div className="col-md-5">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  hidden
                  ref={logoFileInputRef}
                  onChange={handleLogoFileChange}
                />
                <button
                  type="button"
                  disabled={isUploadingLogo}
                  onClick={() => logoFileInputRef.current?.click()}
                  className="btn btn-danger w-100 radius-8 fw-semibold d-flex align-items-center justify-content-center gap-2"
                  style={{ height: "44px", backgroundColor: "#ed1c24", border: "none" }}
                >
                  {isUploadingLogo ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:upload-linear" className="text-xl" />
                      Select & Upload Logo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Uploaded Logos Preview List */}
          <div className="mb-8">
            <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-12">
              Current Logo List ({tempLogos.length})
            </label>
            {tempLogos.length > 0 ? (
              <div
                className="d-flex flex-wrap gap-12 overflow-y-auto p-12 border radius-8"
                style={{ maxHeight: "250px", backgroundColor: "#fafafa" }}
              >
                {tempLogos.map((logo, idx) => (
                  <div
                    key={idx}
                    className="position-relative border radius-8 p-8 d-flex flex-column align-items-center bg-white shadow-sm hover-scale-102 transition-all"
                    style={{ width: "130px", height: "120px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center w-100 overflow-hidden mb-8"
                      style={{ height: "60px" }}
                    >
                      <img
                        src={getImageUrl(logo.image)}
                        alt={logo.companyName || "logo"}
                        className="object-fit-contain w-100 h-100"
                      />
                    </div>
                    <span
                      className="text-xxs text-secondary text-center text-truncate w-100 fw-semibold px-4"
                      title={logo.companyName}
                    >
                      {logo.companyName}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTempLogo(idx)}
                      className="position-absolute btn btn-xs btn-danger p-4 rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        top: "-6px",
                        right: "-6px",
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#ef4444",
                      }}
                      title="Remove Logo"
                    >
                      <Icon icon="ph:x-bold" className="text-xxs text-white" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-24 bg-light border border-dashed radius-8 text-secondary-light text-xs">
                No sponsor/client logos added yet. Use the upload card above.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" className="radius-8 px-24" onClick={closeBrandModal}>
            Cancel
          </Button>
          <button
            onClick={handleSubmitBrand}
            disabled={isSubmitting}
            className="btn btn-danger radius-8 px-32 fw-semibold"
            style={{ backgroundColor: "#ed1c24", border: "none", height: "38px" }}
          >
            {isSubmitting ? "Saving..." : "Save Brand"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-32">
          <div className="d-flex justify-content-center mb-16">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="lucide:trash-2" className="text-xxl text-danger-600" />
            </div>
          </div>
          <h5 className="mb-8 fw-bold text-dark">Delete Expo Brand</h5>
          <p className="text-secondary-light mb-24 text-sm px-16">
            Are you sure you want to delete this Expo Brand? This action cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-24 radius-8"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="px-24 radius-8"
              onClick={handleDeleteBrand}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ExpoBrandLayer;
